import { Module } from 'vuex'
import { Store, Content } from '@/types'
import apiFactory from '@api/content'
import { cloneDeep, get } from 'lodash'

export const SET_CATEGORIES = 'SET_CATEGORIES'
export const SET_CONTENT = 'SET_CONTENT'
export const SET_BOOKMARKS = 'SET_BOOKMARKS'
export const ADD_TO_CACHE = 'ADD_TO_CACHE'
export const REMOVE_BOOKMARK = 'REMOVE_BOOKMARK'
export const SET_SEARCH_RESULTS = 'SET_SEARCH_RESULTS'
export const SET_SEARCH_BAR_VISIBILITY = 'SET_SEARCH_BAR_VISIBILITY'
export const RESTORE_BOOKMARK_STATE = 'RESTORE_BOOKMARK_STATE'

const api = apiFactory()
const defaultState: Content.ModuleState = {
  categories: [],
  content: [],
  contentCache: [],
  bookmarks: [],
  searchResults: [],
  searchBarVisibility: false
}

// const lang = 'en'

const storeModule: Module<Content.ModuleState, Store.RootState> = {
  namespaced: true,
  state: cloneDeep(defaultState),
  mutations: {
    [SET_CATEGORIES]: (state, categories: Content.API.Res.GetCategories) => {
      state.categories = categories
    },
    [SET_CONTENT]: (state, content: Content.API.Res.ContentByCategory) => {
      state.content = content
    },
    [SET_BOOKMARKS]: (state, bookmarks: Content.API.Res.GetBookmarks) => {
      state.bookmarks = bookmarks
    },
    [REMOVE_BOOKMARK]: (state, contentId: string) => {
      const index = state.bookmarks.findIndex(bookmarkItem => bookmarkItem.bookmark.contentId === contentId)
      if (~index) {
        state.bookmarks.splice(index, 1)
      }
    },
    [ADD_TO_CACHE]: (state, article: Content.ContentItem) => {
      state.contentCache.push(article)
    },
    [SET_SEARCH_RESULTS]: (state, searchResults: Content.SimpleContent[]) => {
      state.searchResults = searchResults
    },
    [SET_SEARCH_BAR_VISIBILITY]: (state, visibility: boolean) => {
      state.searchBarVisibility = visibility
    },
    [RESTORE_BOOKMARK_STATE]: (
      state,
      stateBackup: Record<'contentCache' | 'content' | 'bookmarks', any>
    ) => {
      const contentItemForEachHandler = (
        key: 'contentCache' | 'content'
      ) => (
        contentItem: Content.ContentItem
      ) => {
        contentItem.userBookmarked = stateBackup[key][contentItem.cmsContent.id]
      }
      state.content.forEach(contentItemForEachHandler('content'))
      state.contentCache.forEach(contentItemForEachHandler('contentCache'))
      state.bookmarks = stateBackup.bookmarks.slice()
    }
  },
  actions: {
    SetSearchBarVisibility ({ commit }, searchBarVisibility) {
      commit(SET_SEARCH_BAR_VISIBILITY, searchBarVisibility)
    },
    async GetSearchResults ({ commit, rootGetters }, q) {
      const lang = rootGetters['user/settings/lang']
      commit(SET_SEARCH_RESULTS, [])
      commit(SET_SEARCH_RESULTS, await api.searchContent({ lang, q }))
    },
    async GetCategories ({ commit, rootGetters }) {
      const lang = rootGetters['user/settings/lang']
      commit(SET_CATEGORIES, [])
      commit(SET_CATEGORIES, await api.getCategories({ lang }))
    },
    async LoadContent ({ commit, rootGetters }, { categoryId }) {
      const lang = rootGetters['user/settings/lang']
      commit(SET_CONTENT, [])
      commit(SET_CONTENT, await api.contentByCategory({ lang, categoryId }))
    },
    async GetContentItemById ({ commit, getters, rootGetters }, { contentId }) {
      const lang = rootGetters['user/settings/lang']
      // Retrieve content item from one of the sources
      const contentItemRetriever = async (): Promise<Content.ContentItem> => {
        const loadedContentItem = getters.loadedContentItem(contentId)
        if (loadedContentItem) {
          return loadedContentItem
        }
        const cachedContentItem = getters.cachedContentItem(contentId)
        if (cachedContentItem) {
          return cachedContentItem
        }
        const contentItem = await api.getContentItemById({ lang, contentId })
        commit(ADD_TO_CACHE, contentItem)
        return contentItem
      }
      const contentItem = await contentItemRetriever()
      // Augment content item to include retrieved or compiled web content
      let webContent = ''
      if (contentItem.cmsContent.webUrl) {
        const html = await api.getHtmlContent(contentItem.cmsContent.webUrl)
        if (html && /<body>/.test(html)) {
          const match = html.match(/<body>(?<body>[\s\S]+)<\/body>/)
          webContent = get(match, 'groups.body')
        }
      }
      if (!webContent) {
        webContent = contentItem.cmsContent.body.map(({ text }) => text).join('\n')
      }
      contentItem.cmsContent.webContent = webContent
      return contentItem
    },
    async GetBookmarks ({ commit, rootGetters }) {
      const lang = rootGetters['user/settings/lang']
      commit(SET_BOOKMARKS, await api.getBookmarks({ lang }))
    },
    async UpdateBookmark ({ state, commit, dispatch, rootGetters }, { contentId, value, undo = Promise.resolve(false) }) {
      const lang = rootGetters['user/settings/lang']
      // Backup bookmark related part of the state
      const contentItemReducer = (
        acc: Record<string, boolean>,
        contentItem: Content.ContentItem
      ) => ({
        ...acc,
        [contentItem.cmsContent.id]: contentItem.userBookmarked
      })
      const stateBackup = {
        content: state.content.reduce(contentItemReducer, {} as Record<string, boolean>),
        contentCache: state.contentCache.reduce(contentItemReducer, {} as Record<string, boolean>),
        bookmarks: state.bookmarks.slice()
      };
      // Optimistically update content
      [
        state.content,
        state.contentCache
      ]
        .forEach(
          array => {
            const ContentRecord = array.find(
              content => content.cmsContent.id === contentId
            )
            if (ContentRecord) {
              ContentRecord.userBookmarked = value
            }
          }
        )
      // Optimistically remove bookmarks
      if (!value) {
        commit(REMOVE_BOOKMARK, contentId)
      }
      if (!(await undo)) {
        // Update backend
        const method = value
          ? 'createBookmark'
          : 'removeBookmark'
        try {
          await api[method]({ contentId, lang })
          await dispatch('GetBookmarks')
          return true
        }
        catch (error) {
          commit(RESTORE_BOOKMARK_STATE, stateBackup)
          throw error
        }
      }
      else {
        // Undo optimistic update
        commit(RESTORE_BOOKMARK_STATE, stateBackup)
      }
    }
  },
  getters: {
    byKey: state => (key: keyof Content.ModuleState) => state[key],
    categories: state => state.categories,
    categoriesNames: state => state.categories.map(({ name }) => name),
    content: state => state.content,
    loadedContentItem: state => (id: string) => state.content.find(item => item.cmsContent.id === id),
    cachedContentItem: state => (id: string) => state.contentCache.find(item => item.cmsContent.id === id),
    bookmarks: state => state.bookmarks,
    searchResults: state => state.searchResults,
    searchBarVisible: state => state.searchBarVisibility
  }
}

export default storeModule
